import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  ArrowLeft,
  CheckCircle,
  Package,
  Truck,
  Mail,
  Phone,
  Calendar,
  MapPin,
  CreditCard,
  Download,
  Share2,
  Heart,
  Menu,
  X,
  ShoppingBag,
  Gift,
  ExternalLink
} from 'lucide-react'

interface Offer {
  id: number
  title: string
  description: string
  click_url: string
  image: string
  cta_yes: string
  cta_no: string
  advertiser_name: string
  short_description: string
  short_headline: string
}

interface OffersResponse {
  data: {
    offers: Offer[]
  }
}

const MomentsExampleOrderThankYouPage: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // Fetch offers from MomentScience API
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await fetch('https://api.adspostx.com/native/v2/offers.json?api_key=582c2b53-56d5-49a2-a239-7c36e5b0c789', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            country: 'us'
          })
        })

        if (!response.ok) {
          throw new Error('Failed to fetch offers')
        }

        const data: OffersResponse = await response.json()
        setOffers(data.data.offers.slice(0, 3)) // Limit to 3 offers
        setLoading(false)
      } catch (err) {
        setError('Failed to load offers')
        setLoading(false)
        console.error('Error fetching offers:', err)
      }
    }

    fetchOffers()
  }, [])
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
  }

  const orderDetails = {
    orderNumber: "ORD-2025-001247",
    orderDate: "July 21, 2025",
    estimatedDelivery: "July 25, 2025",
    total: "$89.97",
    paymentMethod: "**** 4532",
    shippingAddress: "123 Main Street, New York, NY 10001"
  }

  const orderItems = [
    {
      name: "Premium Wireless Headphones",
      quantity: 1,
      price: "$59.99",
      image: "🎧"
    },
    {
      name: "Phone Case - Clear",
      quantity: 2,
      price: "$24.98",
      image: "📱"
    },
    {
      name: "USB-C Cable",
      quantity: 1,
      price: "$9.99",
      image: "🔌"
    }
  ]

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F9F9', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-[#E5E7EB] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-[#FE4215] to-[#E53910] rounded-lg flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold" style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}>TechStore</h1>
            </div>

            {/* Navigation - Desktop */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }} className="hover:text-[#3B82F6] transition-colors">Shop</a>
              <a href="#" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }} className="hover:text-[#3B82F6] transition-colors">About</a>
              <a href="#" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }} className="hover:text-[#3B82F6] transition-colors">Support</a>
              <a href="#" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }} className="hover:text-[#3B82F6] transition-colors">Account</a>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-md hover:bg-[#F3F3F3]"
              style={{ color: '#6E6D7A' }}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-[#E5E7EB]">
              <div className="flex flex-col space-y-4">
                <a href="#" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }} className="hover:text-[#3B82F6] transition-colors">Shop</a>
                <a href="#" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }} className="hover:text-[#3B82F6] transition-colors">About</a>
                <a href="#" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }} className="hover:text-[#3B82F6] transition-colors">Support</a>
                <a href="#" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }} className="hover:text-[#3B82F6] transition-colors">Account</a>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Back Navigation */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Link
            to="/moments-gallery"
            className="inline-flex items-center space-x-2 hover:text-[#3B82F6] transition-colors duration-300"
            style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Gallery</span>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Success Header */}
          <motion.div
            variants={itemVariants}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-xl mb-6" style={{ backgroundColor: '#F3F3F3', borderRadius: '12px' }}>
              <CheckCircle className="w-12 h-12" style={{ color: '#FE4215' }} />
            </div>
            <h1 
              className="text-4xl sm:text-5xl font-bold mb-4"
              style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
            >
              Order Complete!
            </h1>
            <p 
              className="text-xl max-w-2xl mx-auto"
              style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
            >
              Thank you for your purchase! Your order has been confirmed and we'll send you tracking information once your items ship.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Order Summary - Left Column */}
            <div className="lg:col-span-1 space-y-8">
              {/* Order Details Card */}
              <motion.div
                variants={itemVariants}
                className="bg-white rounded-xl shadow-sm"
                style={{ borderRadius: '12px' }}
              >
                <div className="p-6 border-b border-[#E5E7EB]">
                  <div className="flex items-center justify-between">
                    <h2 
                      className="text-2xl font-bold"
                      style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
                    >
                      Order Summary
                    </h2>
                    <span 
                      className="px-3 py-1 text-sm font-medium rounded-full"
                      style={{ backgroundColor: '#F3F3F3', color: '#FE4215' }}
                    >
                      Confirmed
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <Package className="w-5 h-5" style={{ color: '#6E6D7A' }} />
                        <div>
                          <p className="text-sm" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}>Order Number</p>
                          <p className="font-semibold" style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}>{orderDetails.orderNumber}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5" style={{ color: '#6E6D7A' }} />
                        <div>
                          <p className="text-sm" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}>Order Date</p>
                          <p className="font-semibold" style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}>{orderDetails.orderDate}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CreditCard className="w-5 h-5" style={{ color: '#6E6D7A' }} />
                        <div>
                          <p className="text-sm" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}>Payment Method</p>
                          <p className="font-semibold" style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}>Card ending in {orderDetails.paymentMethod}</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <Truck className="w-5 h-5" style={{ color: '#6E6D7A' }} />
                        <div>
                          <p className="text-sm" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}>Estimated Delivery</p>
                          <p className="font-semibold" style={{ color: '#FE4215', fontFamily: 'Inter, sans-serif' }}>{orderDetails.estimatedDelivery}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-5 h-5" style={{ color: '#6E6D7A' }} />
                        <div>
                          <p className="text-sm" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}>Shipping Address</p>
                          <p className="font-semibold" style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}>{orderDetails.shippingAddress}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Package className="w-5 h-5" style={{ color: '#6E6D7A' }} />
                        <div>
                          <p className="text-sm" style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}>Total Amount</p>
                          <p className="font-semibold text-2xl" style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}>{orderDetails.total}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="border-t border-[#E5E7EB] pt-6">
                    <h3 
                      className="text-lg font-semibold mb-4"
                      style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
                    >
                      Items Ordered
                    </h3>
                    <div className="space-y-4">
                      {orderItems.map((item, index) => (
                        <div key={index} className="flex items-center space-x-4 p-4 rounded-lg" style={{ backgroundColor: '#F3F3F3', borderRadius: '8px' }}>
                          <div className="text-2xl">{item.image}</div>
                          <div className="flex-1">
                            <h4 
                              className="font-semibold"
                              style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
                            >
                              {item.name}
                            </h4>
                            <p 
                              className="text-sm"
                              style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                            >
                              Quantity: {item.quantity}
                            </p>
                          </div>
                          <div 
                            className="text-lg font-semibold"
                            style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
                          >
                            {item.price}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                variants={itemVariants}
                className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
              >
                <button 
                  className="flex-1 inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-all duration-300 font-semibold"
                  style={{ 
                    backgroundColor: '#233D71', 
                    color: 'white', 
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '8px',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1A2B49'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#233D71'}
                >
                  <Download className="w-5 h-5" />
                  <span>Download Receipt</span>
                </button>
                <button 
                  className="flex-1 inline-flex items-center justify-center space-x-2 px-6 py-3 border rounded-lg transition-all duration-300 font-semibold"
                  style={{ 
                    backgroundColor: 'white', 
                    color: '#1A2B49', 
                    borderColor: '#E5E7EB',
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '8px',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F9F9F9'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <Share2 className="w-5 h-5" />
                  <span>Share Order</span>
                </button>
                <button 
                  className="flex-1 inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-all duration-300 font-semibold"
                  style={{ 
                    backgroundColor: '#FE4215', 
                    color: 'white', 
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '8px',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#E23B13'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#FE4215'}
                >
                  <Heart className="w-5 h-5" />
                  <span>Continue Shopping</span>
                </button>
              </motion.div>
            </div>

            {/* Exclusive Offers - Right Column */}
            <div className="lg:col-span-1">
              <motion.div
                variants={itemVariants}
                className="rounded-xl shadow-lg border sticky top-24"
                style={{ 
                  backgroundColor: 'white',
                  borderColor: '#E5E7EB',
                  borderRadius: '10px'
                }}
              >
                <div className="p-6 border-b" style={{ borderColor: '#E5E7EB' }}>
                  <div className="flex items-center space-x-3 mb-2">
                    <Gift className="w-6 h-6" style={{ color: '#9333EA' }} />
                    <h3 
                      className="text-xl font-bold"
                      style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
                    >
                      Exclusive Offers
                    </h3>
                  </div>
                  <p 
                    className="text-sm"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                  >
                    Here are some special offers from brands you love!
                  </p>
                </div>

                <div className="p-6">
                  {loading ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse">
                          <div className="h-20 rounded-lg mb-3" style={{ backgroundColor: '#F3F3F3' }}></div>
                          <div className="h-4 rounded w-3/4 mb-2" style={{ backgroundColor: '#F3F3F3' }}></div>
                          <div className="h-3 rounded w-1/2" style={{ backgroundColor: '#F3F3F3' }}></div>
                        </div>
                      ))}
                    </div>
                  ) : error ? (
                    <div className="text-center py-8">
                      <p 
                        className="mb-4" 
                        style={{ color: '#E53935', fontFamily: 'Inter, sans-serif' }}
                      >
                        {error}
                      </p>
                      <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 rounded-lg transition-colors"
                        style={{ 
                          backgroundColor: '#233D71', 
                          color: 'white', 
                          fontFamily: 'Inter, sans-serif',
                          borderRadius: '8px'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1A2B49'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#233D71'}
                      >
                        Try Again
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {offers.map((offer) => (
                        <div 
                          key={offer.id} 
                          className="border rounded-lg p-4 hover:shadow-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
                          style={{ borderColor: '#E5E7EB', borderRadius: '8px' }}
                        >
                          <div className="flex items-start space-x-4">
                            <img
                              src={offer.image}
                              alt={offer.short_headline}
                              className="w-20 h-20 rounded-lg object-contain flex-shrink-0 p-1 transition-transform duration-300"
                              style={{ backgroundColor: '#F9F9F9', borderRadius: '8px' }}
                              onError={(e) => {
                                e.currentTarget.src = `data:image/svg+xml;base64,${btoa('<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect width="80" height="80" fill="#f9f9f9"/><text x="40" y="40" text-anchor="middle" dy="0.3em" font-family="Inter, sans-serif" font-size="12" fill="#6E6D7A">No Image</text></svg>')}`
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <h4 
                                className="font-semibold text-sm mb-2 line-clamp-2 transition-colors duration-300"
                                style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
                              >
                                {offer.short_headline}
                              </h4>
                              <p 
                                className="text-xs mb-3 line-clamp-2 transition-colors duration-300"
                                style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                              >
                                {offer.short_description}
                              </p>
                              <div className="mb-3">
                                <span 
                                  className="text-xs font-medium transition-colors duration-300"
                                  style={{ color: '#9333EA', fontFamily: 'Inter, sans-serif' }}
                                >
                                  {offer.advertiser_name}
                                </span>
                              </div>
                              <button
                                onClick={() => window.open(offer.click_url, '_blank')}
                                className="w-full inline-flex items-center justify-center space-x-1 px-3 py-2 text-xs font-medium rounded-md transition-all duration-300 transform hover:scale-105"
                                style={{ 
                                  backgroundColor: '#9333EA', 
                                  color: 'white', 
                                  fontFamily: 'Inter, sans-serif',
                                  borderRadius: '6px'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7E22CE'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#9333EA'}
                              >
                                <span>{offer.cta_yes}</span>
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>

          {/* What's Next Section */}
          <motion.div
            variants={itemVariants}
            className="mt-12 rounded-xl shadow-lg border"
            style={{ 
              backgroundColor: 'white',
              borderColor: '#E5E7EB', 
              borderRadius: '10px'
            }}
          >
            <div className="p-6 border-b" style={{ borderColor: '#E5E7EB' }}>
              <h2 
                className="text-2xl font-bold"
                style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
              >
                What's Next?
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div 
                    className="inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4"
                    style={{ backgroundColor: '#EDF5FF', borderRadius: '8px' }}
                  >
                    <Package className="w-6 h-6" style={{ color: '#233D71' }} />
                  </div>
                  <h3 
                    className="font-semibold mb-2"
                    style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
                  >
                    Processing
                  </h3>
                  <p 
                    className="text-sm"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                  >
                    We're carefully preparing your items for shipment
                  </p>
                </div>
                <div className="text-center">
                  <div 
                    className="inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4"
                    style={{ backgroundColor: '#FFF9E6', borderRadius: '8px' }}
                  >
                    <Truck className="w-6 h-6" style={{ color: '#F59E0B' }} />
                  </div>
                  <h3 
                    className="font-semibold mb-2"
                    style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
                  >
                    Shipping
                  </h3>
                  <p 
                    className="text-sm"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                  >
                    You'll receive tracking information via email
                  </p>
                </div>
                <div className="text-center">
                  <div 
                    className="inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4"
                    style={{ backgroundColor: '#E8F5E9', borderRadius: '8px' }}
                  >
                    <CheckCircle className="w-6 h-6" style={{ color: '#2E7D32' }} />
                  </div>
                  <h3 
                    className="font-semibold mb-2"
                    style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
                  >
                    Delivered
                  </h3>
                  <p 
                    className="text-sm"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                  >
                    Enjoy your new items!
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Support */}
          <motion.div
            variants={itemVariants}
            className="mt-8 text-center"
          >
            <div className="rounded-xl p-6" style={{ backgroundColor: '#F0F7FF', borderRadius: '10px' }}>
              <h3 
                className="text-lg font-semibold mb-4"
                style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
              >
                Need Help with Your Order?
              </h3>
              <p 
                className="mb-6"
                style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
              >
                Our customer support team is here to help with any questions or concerns.
              </p>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 justify-center">
                <a
                  href="mailto:support@techstore.com"
                  className="inline-flex items-center space-x-2 transition-colors duration-300"
                  style={{ color: '#233D71', fontFamily: 'Inter, sans-serif' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#1A2B49'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#233D71'}
                >
                  <Mail className="w-5 h-5" />
                  <span>support@techstore.com</span>
                </a>
                <a
                  href="tel:+1234567890"
                  className="inline-flex items-center space-x-2 transition-colors duration-300"
                  style={{ color: '#233D71', fontFamily: 'Inter, sans-serif' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#1A2B49'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#233D71'}
                >
                  <Phone className="w-5 h-5" />
                  <span>(123) 456-7890</span>
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16" style={{ backgroundColor: 'white', borderColor: '#E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ 
                    background: 'linear-gradient(135deg, #233D71 0%, #FE4215 100%)',
                    borderRadius: '8px'
                  }}
                >
                  <ShoppingBag className="w-5 h-5 text-white" />
                </div>
                <h3 
                  className="text-xl font-bold"
                  style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
                >
                  TechStore
                </h3>
              </div>
              <p 
                className="mb-4 max-w-md"
                style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
              >
                Your trusted destination for the latest tech gadgets and accessories. 
                We provide quality products with excellent customer service.
              </p>
              <div className="flex space-x-4">
                <a 
                  href="#" 
                  className="transition-colors"
                  style={{ color: '#6E6D7A' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                >
                  <span className="sr-only">Facebook</span>
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="#" 
                  className="transition-colors"
                  style={{ color: '#6E6D7A' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                >
                  <span className="sr-only">Twitter</span>
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 
                className="text-sm font-semibold uppercase tracking-wider mb-4"
                style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
              >
                Quick Links
              </h4>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="#" 
                    className="transition-colors"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="transition-colors"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                  >
                    Contact
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="transition-colors"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                  >
                    Careers
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="transition-colors"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                  >
                    Press
                  </a>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 
                className="text-sm font-semibold uppercase tracking-wider mb-4"
                style={{ color: '#1A2B49', fontFamily: 'Inter, sans-serif' }}
              >
                Support
              </h4>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="#" 
                    className="transition-colors"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                  >
                    Help Center
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="transition-colors"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                  >
                    Shipping Info
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="transition-colors"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                  >
                    Returns
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className="transition-colors"
                    style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                  >
                    Track Order
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t" style={{ borderColor: '#E5E7EB' }}>
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p 
                className="text-sm"
                style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
              >
                © 2025 TechStore. All rights reserved.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <a 
                  href="#" 
                  className="text-sm transition-colors"
                  style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                >
                  Privacy Policy
                </a>
                <a 
                  href="#" 
                  className="text-sm transition-colors"
                  style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#233D71'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#6E6D7A'}
                >
                  Terms of Service
                </a>
                <p 
                  className="text-sm"
                  style={{ color: '#6E6D7A', fontFamily: 'Inter, sans-serif' }}
                >
                  Offers powered by <span className="font-medium" style={{ color: '#9333EA' }}>MomentScience</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default MomentsExampleOrderThankYouPage
